import React, { useState } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import Sticky from 'react-stickynode'
import { Image } from '@/atoms/Image'
import { BoundedImage } from '@/molecules/BoundedImage'
import { CatalogImage } from '@/services/ContentCatalog'
import { PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { ProjectMeta } from '@/services/ProjectsService'
import { FilmDetails as FilmDetailsType, TheatricalRegion } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useWatchTheatricalContext } from '@/views/WatchTheatricalView/WatchTheatricalContext'
import { Ctas } from '@/views/WatchTheatricalView/ctas'

interface StickyCtaProps {
  titleLogo: CatalogImage | undefined
  projectName: string
  heroCta: ReturnType<typeof useWatchTheatricalContext>['heroCta']
  userCountry: string
  projectMeta: ProjectMeta
  filmDetails: FilmDetailsType
  allTheatricalReleaseRegions: TheatricalRegion[]
  hasEarlyAccess: boolean
  isInTheaters?: boolean
  isStreaming?: boolean
  canShowTickets?: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  currentPhaseSlug?: PhaseSlugForTracking
}

export const StickyCta: React.FC<StickyCtaProps> = ({
  titleLogo,
  projectName,
  projectMeta,
  heroCta,
  userCountry,
  filmDetails,
  allTheatricalReleaseRegions,
  hasEarlyAccess,
  isInTheaters,
  isStreaming,
  canShowTickets,
  logo,
  currentPhaseSlug,
}) => {
  const canShowTitleLogo = Boolean(titleLogo)
  const [isStickyFixed, setIsStickyFixed] = useState(false)

  return (
    <>
      <Sticky
        className="hidden max-h-0 overflow-hidden md:block"
        activeClass="relative z-[98] !max-h-[100px]"
        onStateChange={({ status }) => {
          if (status === Sticky.STATUS_FIXED) {
            setIsStickyFixed(true)
          } else {
            setIsStickyFixed(false)
          }
        }}
      >
        <div
          className={classNames(
            'bg-black/80 backdrop-blur-md w-full flex justify-between align-center z-[99] relative overflow-hidden p-6 items-center',
          )}
        >
          <Switch>
            <Case condition={canShowTitleLogo}>
              {titleLogo && (
                <BoundedImage
                  className="flex items-center justify-center md:justify-start"
                  alt={projectName}
                  path={titleLogo.cloudinaryPath}
                  width={500}
                  height={140}
                  sizes="(max-width: 1024px) 30px, (max-width: 1280px) 50px, 50px"
                  imageClassName="max-h-[30px] md:max-h-[32px] lg:max-h-[36px] lg:max-w-fit shortDesktop:max-h-[50px]"
                />
              )}
            </Case>
            <Case condition={!canShowTitleLogo && Array.isArray(logo)}>
              <div className="max-w-[110px] lg:max-w-[140px]">
                {Array.isArray(logo) && (
                  <Image
                    alt={projectName}
                    src={getCloudinaryImageUrl({
                      path: logo[0]?.url ?? '',
                      transforms: 'e_trim',
                    })}
                    width={logo[0]?.width}
                    height={logo[0]?.height}
                    className="max-h-[46px] w-auto"
                  />
                )}
              </div>
            </Case>
          </Switch>
          <Ctas
            heroCta={heroCta}
            userRegionCode={userCountry}
            project={projectMeta}
            filmDetails={filmDetails as FilmDetailsType}
            allTheatricalReleaseRegions={allTheatricalReleaseRegions}
            hasEarlyAccess={hasEarlyAccess}
            isInTheaters={isInTheaters}
            isStreaming={isStreaming}
            canShowTickets={canShowTickets}
            isStickyFixed={isStickyFixed}
            currentPhaseSlug={currentPhaseSlug}
          />
        </div>
      </Sticky>
      <Sticky className="max-h-0 overflow-hidden md:hidden" activeClass="relative z-[98] !max-h-[100px]">
        <div
          className={classNames(
            'bg-black/80 backdrop-blur-md gap-3 w-full flex justify-between align-center z-[99] relative overflow-hidden py-6 px-3 items-center',
          )}
        >
          <Switch>
            <Case condition={canShowTitleLogo}>
              {titleLogo && (
                <BoundedImage
                  className="mb-2 flex items-center justify-center md:justify-start"
                  alt={projectName}
                  path={titleLogo.cloudinaryPath}
                  width={500}
                  height={140}
                  sizes="(max-width: 1024px) 30px, (max-width: 1280px) 50px, 50px"
                  imageClassName="max-h-[30px] lg:max-h-[50px] lg:max-w-fit shortDesktop:max-h-[50px]"
                />
              )}
            </Case>
            <Case condition={!canShowTitleLogo && Array.isArray(logo)}>
              <div className="max-w-[110px] lg:max-w-[140px]">
                {Array.isArray(logo) && (
                  <Image
                    alt={projectName}
                    src={getCloudinaryImageUrl({
                      path: logo[0]?.url ?? '',
                      transforms: 'e_trim',
                    })}
                    width={logo[0]?.width}
                    height={logo[0]?.height}
                    className="max-h-[50px] w-auto"
                  />
                )}
              </div>
            </Case>
          </Switch>

          <Ctas
            heroCta={heroCta}
            userRegionCode={userCountry}
            project={projectMeta}
            filmDetails={filmDetails as FilmDetailsType}
            allTheatricalReleaseRegions={allTheatricalReleaseRegions}
            className="min-h-[46px] py-1 text-center sm:px-16 sm:py-3"
            hasEarlyAccess={hasEarlyAccess}
            isInTheaters={isInTheaters}
            isStreaming={isStreaming}
            canShowTickets={canShowTickets}
            currentPhaseSlug={currentPhaseSlug}
          />
        </div>
      </Sticky>
    </>
  )
}
